<template>
  <ElButton
    class="action-button"
    :class="componentClass"
    :size="size"
    :text="modificationProps.transparent && modificationProps.borderless"
    :plain="modificationProps.transparent && !modificationProps.borderless"
    :type="buttonType"
    :circle="circle"
    :round="modificationProps.round"
    :disabled="disabled || loading"
    :loading="loading"
    :title="tooltip"
  >
    <template
      v-if="loading"
      #loading
    >
      <Icon
        icon="loader"
        :size="size"
        class="is-loading"
      />
    </template>
    <template
      v-if="$slots.icon || icon"
      #icon
    >
      <slot name="icon">
        <Icon
          v-if="icon"
          :icon="icon"
          :size="size"
        />
      </slot>
    </template>
    <slot>
      {{ title ? title : '' }}
    </slot>
  </ElButton>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { ElButton, type ButtonProps as ElButtonProps } from 'element-plus'
import Icon from '@/components/basic/Icon/Icon.vue'
import type { ElSize } from '@/components/basic/types'


interface ButtonProps {
  modification?: 'default' | 'editor' | 'submit' | 'panel'
  size?: ElSize
  title?: string
  icon?: string
  circle?: ElButtonProps['circle']
  disabled?: boolean
  loading?: ElButtonProps['loading']
  danger?: boolean
  tooltip?: string
}

const {
  modification = 'default',
  size = 'default',
  title = '',
  icon = '',
  circle = false,
  disabled = false,
  loading = false,
  danger = false,
} = defineProps<ButtonProps>()

const modifications = {
  editor: {
    transparent: false,
    borderless: false,
    round: false,
  },
  submit: {
    transparent: true,
    borderless: false,
    round: true,
  },
  default: {
    transparent: true,
    borderless: false,
    round: false,
  },
  panel: {
    transparent: true,
    borderless: true,
    round: false,
  },
}

const modificationProps = computed(() => modifications[modification])

const componentClass = computed(() => {
  const res: string[] = []
  if (modificationProps.value.transparent) {
    res.push('transparent')
  }

  return res
})

const buttonType = computed(() => {
  if (modification === 'editor' && danger) {
    return 'danger'
  } else if (!modificationProps.value.transparent) {
    return 'primary'
  } else {
    return 'default'
  }
})

</script>

<style scoped>
.action-button {
  &.transparent:not(:active, .is-text) {
    background: transparent;
  }

  &.transparent:active {
    background: var(--el-fill-color-light); /* should be transparent */
  }
}
</style>
